import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { formatBRL, parseBRLInput } from '@caixa/lib/format';
import { numberToMoneyMask } from '@caixa/lib/moneyMask';
import { MoneyInput } from '@caixa/components/MoneyInput';
import type { ValoresPagamento, MetodoPagamento } from '@caixa/types';

const METODOS: { key: MetodoPagamento; label: string }[] = [
  { key: 'pix', label: 'Pix' },
  { key: 'dinheiro', label: 'Dinheiro' },
  { key: 'credito', label: 'Crédito' },
  { key: 'debito', label: 'Débito' },
];

interface SplitPaymentModalProps {
  open: boolean;
  total: number;
  onConfirm: (valores: ValoresPagamento, totalComDesconto?: number) => void | Promise<void>;
  onCancel: () => void;
}

const initialInputs: Record<MetodoPagamento, string> = {
  pix: '',
  dinheiro: '',
  credito: '',
  debito: '',
};

const initialJaClicou: Record<MetodoPagamento, boolean> = {
  pix: false,
  dinheiro: false,
  credito: false,
  debito: false,
};

function roundCentavos(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Valor para exibir no campo (pt-BR, 2 casas). */
function formatInputNumber(n: number): string {
  return numberToMoneyMask(n) || '0,00';
}

export function SplitPaymentModal({ open, total, onConfirm, onCancel }: SplitPaymentModalProps) {
  const [inputs, setInputs] = useState<Record<MetodoPagamento, string>>({ ...initialInputs });
  /** Após o 1º clique no método, o botão passa de "Dividir" para "Restante". */
  const [jaClicouDividir, setJaClicouDividir] =
    useState<Record<MetodoPagamento, boolean>>({ ...initialJaClicou });
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const valores = useMemo<ValoresPagamento>(() => ({
    pix: roundCentavos(parseBRLInput(inputs.pix)),
    dinheiro: roundCentavos(parseBRLInput(inputs.dinheiro)),
    credito: roundCentavos(parseBRLInput(inputs.credito)),
    debito: roundCentavos(parseBRLInput(inputs.debito)),
  }), [inputs.pix, inputs.dinheiro, inputs.credito, inputs.debito]);

  useEffect(() => {
    if (open) {
      setInputs({ ...initialInputs });
      setJaClicouDividir({ ...initialJaClicou });
      setErro('');
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
    setLoading(false);
  }, [open]);

  const totalVenda = total;
  const pago = roundCentavos((valores.pix || 0) + (valores.dinheiro || 0) + (valores.credito || 0) + (valores.debito || 0));
  const diff = roundCentavos(pago - totalVenda);
  const canConfirm = diff >= 0;

  /** Total da venda menos o que já está nos outros métodos (não inclui `metodo`). */
  const valorRestanteParaMetodo = useCallback(
    (metodo: MetodoPagamento) => {
      let somaOutros = 0;
      for (const { key } of METODOS) {
        if (key === metodo) continue;
        somaOutros += valores[key];
      }
      return roundCentavos(Math.max(0, totalVenda - somaOutros));
    },
    [valores, totalVenda]
  );

  /**
   * Preenche o campo com o valor restante da venda para esse meio
   * (total da venda - soma dos outros métodos). No 1º clique o rótulo vira "Restante".
   */
  const aplicarDividirOuRestante = useCallback(
    (metodo: MetodoPagamento) => {
      const valorNoClique = jaClicouDividir[metodo]
        ? valorRestanteParaMetodo(metodo)
        : roundCentavos(totalVenda / 2);
      setInputs((prev) => ({
        ...prev,
        [metodo]: formatInputNumber(valorNoClique),
      }));
      setJaClicouDividir((prev) => ({ ...prev, [metodo]: true }));
      setErro('');
    },
    [jaClicouDividir, totalVenda, valorRestanteParaMetodo]
  );

  const handleConfirm = useCallback(async () => {
    if (!canConfirm) {
      setErro('O total pago deve ser igual ou maior que o total da venda.');
      return;
    }
    setLoading(true);
    setErro('');
    try {
      await onConfirm(valores, total);
    } catch {
      /* erro exibido pelo handler pai */
    } finally {
      setLoading(false);
    }
  }, [canConfirm, valores, total, onConfirm]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
      if (e.key === 'Enter' && canConfirm && !loading) {
        e.preventDefault();
        void handleConfirm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel, canConfirm, loading, handleConfirm]);

  const indicadorTexto = diff < 0
    ? `Restam: ${formatBRL(Math.abs(diff))}`
    : diff === 0
      ? `Restam: ${formatBRL(0)}`
      : `Troco: ${formatBRL(diff)}`;

  if (!open) return null;

  return (
    <div
      className="caixa-overlay caixa-overlay--60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="split-payment-title"
    >
      <div className="caixa-modal caixa-modal--md caixa-modal--stack">
        <h2 id="split-payment-title" className="sr-only">
          Dividir pagamento
        </h2>
        <div className="caixa-modal-section--head" style={{ display: "block" }}>
          <span className="caixa-label">Total da venda</span>
          <div className="text-2xl font-bold text-[#f5f5f5] mt-1">{formatBRL(total)}</div>
        </div>
        <div className="caixa-modal-section--body space-y-3">
          {METODOS.map(({ key, label }, index) => (
            <div key={key} className="flex gap-2 items-center">
              <label className="caixa-label w-24 shrink-0 mb-0">{label}:</label>
              <MoneyInput
                ref={index === 0 ? firstInputRef : undefined}
                value={inputs[key]}
                onChange={(v) =>
                  setInputs((prev) => ({ ...prev, [key]: v }))
                }
                className="caixa-input min-w-0 flex-1"
              />
              <button
                type="button"
                onClick={() => aplicarDividirOuRestante(key)}
                className="caixa-btn-ghost shrink-0"
                style={{ flex: "0 0 auto" }}
              >
                {jaClicouDividir[key] ? 'Restante' : 'Dividir'}
              </button>
            </div>
          ))}
        </div>
        {erro && (
          <div className="px-4 caixa-error">{erro}</div>
        )}
        <div className="caixa-modal-section--foot" style={{ justifyContent: "space-between" }}>
          <span className="text-sm text-[#a3a3a3] whitespace-nowrap">{indicadorTexto}</span>
          <div className="caixa-modal-footer" style={{ marginTop: 0, flex: 1, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onCancel}
              className="caixa-btn-outline"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canConfirm || loading}
              className="caixa-btn-success"
            >
              {loading ? 'Finalizando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
